import { CSSProperties } from 'react'
import TableElement from "elements/TableElement"
import Sty from "modules/Sty"

const PrivacyAndTerms = () => {
    /** Column definitions for the privacy policy table */
    const columnDefs = [
        { field: 'dataType', title: 'Data Type', flex: 1.2 },
        { field: 'collected', title: 'Collected', flex: 1 },
        { field: 'tracking', title: 'Used for Tracking', flex: 1 },
        { field: 'linkedToYou', title: 'Linked to You', flex: 1 },
        { field: 'sharedWithThird', title: 'Shared with Third Parties', flex: 1.2 }
    ]

    /** Data for the privacy policy table */
    const rowData = [
        { dataType: 'Name', collected: 'Yes', tracking: 'No', linkedToYou: 'Yes', sharedWithThird: 'Yes' },
        { dataType: 'Email', collected: 'Yes', tracking: 'No', linkedToYou: 'Yes', sharedWithThird: 'Yes' },
        { dataType: 'Phone Number', collected: 'Yes', tracking: 'No', linkedToYou: 'Yes', sharedWithThird: 'Yes' },
        { dataType: 'Location (IP-based)', collected: 'Yes', tracking: 'Yes', linkedToYou: 'Yes', sharedWithThird: 'Yes' },
        { dataType: 'Purchase History', collected: 'Yes', tracking: 'No', linkedToYou: 'Yes', sharedWithThird: 'Yes' },
        { dataType: 'Usage Data (App interactions, device info)', collected: 'Yes', tracking: 'Yes', linkedToYou: 'Yes', sharedWithThird: 'Yes' },
        { dataType: 'Identifiers (Device ID, IP, social media logins)', collected: 'Yes', tracking: 'Yes', linkedToYou: 'Yes', sharedWithThird: 'Yes' },
        { dataType: 'Payments (via Stripe or PayPal)', collected: 'Yes', tracking: 'No', linkedToYou: 'No (processed by third party)', sharedWithThird: 'No' },
        { dataType: 'Listening History (Spotify, Apple Music, Deezer)', collected: 'Yes', tracking: 'Yes', linkedToYou: 'Yes', sharedWithThird: 'Yes' },
        { dataType: 'Sensitive Data', collected: 'No', tracking: '—', linkedToYou: '—', sharedWithThird: '—' },
        { dataType: 'Data for Advertising/Marketing', collected: 'Yes', tracking: 'Yes', linkedToYou: 'Yes', sharedWithThird: 'Yes' },
        { dataType: 'Third-Party Analytics/Ad Tracking', collected: 'Yes', tracking: 'Yes', linkedToYou: 'Yes', sharedWithThird: 'Yes' }
    ]

    return (
        <div className="privacyAndTermsComponent" style={sty.privacyAndTermsComponent}>
            {/* Header */}
            <div>
                <div><span style={Sty.text16bold}>Effective Date:</span> July 22, 2024</div>
                <div><span style={Sty.text16bold}>Last Updated:</span> Apr 8, 2025</div>
            </div>
            <div style={{ fontStyle: 'italic' }}>
                SymphonyOS values your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your personal information when you use our mobile application or services through the Apple App Store. By using SymphonyOS, you consent to the data practices described in this Privacy Policy.
                <br />
                <br />
                This policy complies with applicable data protection laws and Apple App Store requirements.
            </div>

            {/* Table */}
            <br />
            <div style={Sty.text24bold}>
                Quick Summary (Apple-Specific Disclosure Table)
            </div>
            <TableElement
                className="privacyTable"
                rowData={rowData}
                columnDefs={columnDefs}
                pagination={false}
                minWidth={800}
            />
            <div>
                You can find Apple's app privacy labels for SymphonyOS within the App Store.
            </div>
            <br />

            {/* Key Additions for App Store Compliance */}
            <div style={Sty.text24bold}>
                Key Additions for App Store Compliance
            </div>
            <div style={Sty.text20bold}>
                1. Data Linked to You
            </div>
            <div>
                We may collect data that Apple defines as "linked to you," such as your name, email, purchase history, listening history, and interactions with the app. This data is used to provide and improve services, personalize content, and fulfill legal obligations.
            </div>
            <br />
            <div style={Sty.text20bold}>
                2. Tracking and Opt-Out
            </div>
            <div>
                We may use third-party services (e.g., Google Analytics, Facebook SDK, Mixpanel) for advertising and analytics. These may track you across apps and websites.
            </div>
            <div>
                You can opt-out of tracking by:
            </div>
            <ul className="bulletList" style={sty.bulletList}>
                <li>Adjusting your settings in your device: <span style={Sty.text16bold}>Settings → Privacy → Tracking</span></li>
                <li>Implementing Apple's <a href="https://developer.apple.com/app-store/user-privacy-and-data-use/" target="_blank" rel="noopener noreferrer">App Tracking Transparency (ATT)</a></li>
                <li>Emailing us at <span style={Sty.text16bold}>team@symphonyos.to</span></li>
            </ul>
            <br />
            {/* Children's Privacy (COPPA Compliance) */}
            <div style={Sty.text20bold}>
                3. Children’s Privacy (COPPA Compliance)
            </div>
            <div>
                We do <span style={Sty.text16bold}>not knowingly collect or solicit personal information from children</span> under the age of 13 through our app. If you believe we have collected such information, please contact us at <span style={Sty.text16bold}>legal@symphonyos.co</span> and we will delete it immediately.
            </div>
            <br />
            <div style={Sty.text24bold}>
                Additional Requirements Covered
            </div>
            <ul className="bulletList" style={sty.bulletList}>
                <li>A link to this Privacy Policy is available within the app under <span style={Sty.text16bold}>Settings → Privacy</span>.</li>
                <li>Users are clearly informed if their data is used for tracking.</li>
                <li>Consent is obtained before tracking begins (via iOS App Tracking Transparency prompt).</li>
                <li>Third-party SDKs used in the app are documented and comply with Apple's data use guidelines.</li>
            </ul>
            <br />
            <div style={Sty.text24bold}>
                Data Deletion Requests
            </div>
            <div>
                You may request deletion of your account and associated data by:
            </div>
            <ul className="bulletList" style={sty.bulletList}>
                <li>Emailing: <span style={Sty.text16bold}>team@symphonyos.to</span></li>
                <li>Within the app: Go to <span style={Sty.text16bold}>Settings → Account → Delete Account</span></li>
                <li>We will process your request within a reasonable timeframe.</li>
            </ul>

            <div>
                We will process your request within 30 days as required under global privacy laws.
            </div>
        </div>
    )
}

const sty: Record<string, CSSProperties> = {
    privacyAndTermsComponent: {
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
        maxWidth: '100%',
        overflowX: 'auto'
    },
    bulletList: {
        paddingLeft: 36,
        listStyleType: 'disc'
    }
}

export default PrivacyAndTerms